import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import {
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaFileDownload,
  FaSearch,
  FaUserCheck,
  FaUserTimes,
  FaClock,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaSync,
} from 'react-icons/fa';

const AttendanceCalendar = () => {
  // Navigation & View state
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [currentDate, setCurrentDate] = useState(moment());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL'); // for daily view

  // Data state
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  // Compute date range based on active tab
  const dateRange = useMemo(() => {
    if (activeTab === 'daily') {
      const dayStr = currentDate.format('YYYY-MM-DD');
      return {
        startDate: dayStr,
        endDate: dayStr,
        label: currentDate.format('dddd, MMMM D, YYYY'),
      };
    } else if (activeTab === 'weekly') {
      const start = currentDate.clone().startOf('isoWeek');
      const end = currentDate.clone().endOf('isoWeek');
      return {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        label: `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`,
        days: Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'days')),
      };
    } else {
      const start = currentDate.clone().startOf('month');
      const end = currentDate.clone().endOf('month');
      return {
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        label: currentDate.format('MMMM YYYY'),
        daysInMonth: currentDate.daysInMonth(),
      };
    }
  }, [activeTab, currentDate]);

  // Fetch staff and submissions
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getJwtToken();
      const headers = token ? { Authorization: token } : {};

      const [staffRes, submissionsRes] = await Promise.all([
        axios.get(`${END_POINT}/attendance/members`, { headers }).catch((err) => {
          console.error('Error fetching staff list:', err);
          return { data: [] };
        }),
        axios
          .get(`${END_POINT}/attendancesubmit`, {
            params: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            },
            headers,
          })
          .catch((err) => {
            console.error('Error fetching attendance submissions:', err);
            return { data: { data: [] } };
          }),
      ]);

      const staffData = Array.isArray(staffRes.data)
        ? staffRes.data
        : staffRes.data?.data || [];
      const subData = Array.isArray(submissionsRes.data)
        ? submissionsRes.data
        : submissionsRes.data?.data || [];

      setStaffList(staffData);
      setSubmissions(subData);
    } catch (err) {
      console.error('Failed to load attendance counts data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.startDate, dateRange.endDate, activeTab]);

  // Helper to calculate duration in minutes and formatted string
  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return { minutes: 0, text: '-' };
    const start = moment(checkIn);
    const end = moment(checkOut);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return { minutes: 0, text: '-' };
    }
    const diffMins = end.diff(start, 'minutes');
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return {
      minutes: diffMins,
      text: `${hours}h ${mins}m`,
    };
  };

  // Build staff records with attendance map
  const processedStaff = useMemo(() => {
    // Collect all positions
    const staffMap = new Map();

    staffList.forEach((s) => {
      staffMap.set(String(s._id), {
        id: String(s._id),
        name: s.name || 'Unnamed Staff',
        position: s.position || 'General',
        joinDate: s.joinDate ? moment(s.joinDate).format('YYYY-MM-DD') : '-',
        attendanceByDate: {},
      });
    });

    // Populate submissions
    submissions.forEach((sub) => {
      const rawStaffId = sub.staffId;
      const staffId =
        typeof rawStaffId === 'object' && rawStaffId?._id
          ? String(rawStaffId._id)
          : String(rawStaffId || '');

      if (!staffId) return;

      if (!staffMap.has(staffId)) {
        staffMap.set(staffId, {
          id: staffId,
          name:
            sub.name ||
            (typeof rawStaffId === 'object' ? rawStaffId?.name : null) ||
            'Staff',
          position:
            (typeof rawStaffId === 'object' ? rawStaffId?.position : null) ||
            'General',
          joinDate: '-',
          attendanceByDate: {},
        });
      }

      const recDate = sub.date || sub.checkIn;
      if (!recDate) return;
      const dateKey = moment(recDate).format('YYYY-MM-DD');

      const dur = calculateDuration(sub.checkIn, sub.checkOut);

      staffMap.get(staffId).attendanceByDate[dateKey] = {
        status: (sub.status || 'unknown').toLowerCase(),
        checkIn: sub.checkIn ? moment(sub.checkIn).format('hh:mm A') : '-',
        checkOut: sub.checkOut ? moment(sub.checkOut).format('hh:mm A') : '-',
        durationText: dur.text,
        durationMinutes: dur.minutes,
        notes: sub.notes || '',
      };
    });

    return Array.from(staffMap.values());
  }, [staffList, submissions]);

  // Positions list for filtering
  const positionsList = useMemo(() => {
    const set = new Set();
    processedStaff.forEach((s) => {
      if (s.position) set.add(s.position);
    });
    return Array.from(set);
  }, [processedStaff]);

  // Filtered staff based on search query & position filter
  const filteredStaff = useMemo(() => {
    return processedStaff.filter((s) => {
      const matchesSearch =
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.position.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesPos =
        selectedPosition === 'ALL' || s.position === selectedPosition;

      if (!matchesSearch || !matchesPos) return false;

      // Status filter in Daily view
      if (activeTab === 'daily' && selectedStatus !== 'ALL') {
        const todayRec = s.attendanceByDate[dateRange.startDate];
        const status = todayRec ? todayRec.status : 'not-marked';
        if (selectedStatus === 'present' && status !== 'present') return false;
        if (selectedStatus === 'absent' && status !== 'absent') return false;
        if (
          selectedStatus === 'half-day' &&
          status !== 'half-day' &&
          status !== 'on-leave'
        )
          return false;
        if (selectedStatus === 'not-marked' && status !== 'not-marked')
          return false;
      }

      return true;
    });
  }, [processedStaff, searchQuery, selectedPosition, selectedStatus, activeTab, dateRange.startDate]);

  // ==================== DAILY CALCULATIONS ====================
  const dailyMetrics = useMemo(() => {
    const dayKey = dateRange.startDate;
    const totalStaff = processedStaff.length;
    let present = 0;
    let absent = 0;
    let halfDay = 0;
    let notMarked = 0;

    processedStaff.forEach((s) => {
      const att = s.attendanceByDate[dayKey];
      if (!att) {
        notMarked++;
      } else if (att.status === 'present') {
        present++;
      } else if (att.status === 'absent') {
        absent++;
      } else if (att.status === 'half-day' || att.status === 'on-leave') {
        halfDay++;
      } else {
        notMarked++;
      }
    });

    const presentRate =
      totalStaff > 0 ? ((present / totalStaff) * 100).toFixed(1) : 0;

    return { totalStaff, present, absent, halfDay, notMarked, presentRate };
  }, [processedStaff, dateRange.startDate]);

  // ==================== WEEKLY CALCULATIONS ====================
  const weeklyData = useMemo(() => {
    if (activeTab !== 'weekly' || !dateRange.days) return { staffRows: [], metrics: {} };

    const days = dateRange.days;
    let totalPresentShifts = 0;
    let totalAbsentShifts = 0;
    let totalHalfDayShifts = 0;
    let totalPossibleShifts = 0;

    const staffRows = filteredStaff.map((staff) => {
      let presentDays = 0;
      let absentDays = 0;
      let halfDays = 0;
      let markedDays = 0;
      let totalMinutes = 0;

      const dailyBreakdown = days.map((day) => {
        const dStr = day.format('YYYY-MM-DD');
        const att = staff.attendanceByDate[dStr];

        if (att) {
          markedDays++;
          if (att.status === 'present') {
            presentDays++;
            totalPresentShifts++;
          } else if (att.status === 'absent') {
            absentDays++;
            totalAbsentShifts++;
          } else if (att.status === 'half-day' || att.status === 'on-leave') {
            halfDays++;
            totalHalfDayShifts++;
          }
          totalMinutes += att.durationMinutes || 0;
        }

        totalPossibleShifts++;

        return {
          dateStr: dStr,
          dayName: day.format('ddd'),
          dayNum: day.format('D'),
          isToday: day.isSame(moment(), 'day'),
          record: att || null,
        };
      });

      const effectivePresent = presentDays + halfDays * 0.5;
      const rate =
        markedDays > 0 ? ((effectivePresent / markedDays) * 100).toFixed(1) : '0.0';

      return {
        ...staff,
        dailyBreakdown,
        presentDays,
        absentDays,
        halfDays,
        markedDays,
        totalMinutes,
        totalHoursText: `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`,
        rate,
      };
    });

    const totalStaff = filteredStaff.length;
    const avgAttendanceRate =
      totalPossibleShifts > 0
        ? (
            ((totalPresentShifts + totalHalfDayShifts * 0.5) /
              (totalPresentShifts + totalAbsentShifts + totalHalfDayShifts || 1)) *
            100
          ).toFixed(1)
        : '0.0';

    return {
      staffRows,
      metrics: {
        totalStaff,
        avgAttendanceRate,
        totalPresentShifts,
        totalAbsentShifts,
        totalHalfDayShifts,
      },
    };
  }, [activeTab, dateRange, filteredStaff]);

  // ==================== MONTHLY CALCULATIONS ====================
  const monthlyData = useMemo(() => {
    if (activeTab !== 'monthly') return { staffRows: [], metrics: {} };

    const start = currentDate.clone().startOf('month');
    const end = currentDate.clone().endOf('month');
    const totalDays = dateRange.daysInMonth || 30;

    // Count distinct dates with records
    const loggedDatesSet = new Set();
    submissions.forEach((sub) => {
      const d = sub.date || sub.checkIn;
      if (d) loggedDatesSet.add(moment(d).format('YYYY-MM-DD'));
    });
    const clinicRecordedDays = loggedDatesSet.size || totalDays;

    let totalPresentSum = 0;
    let totalAbsentSum = 0;
    let totalHalfDaySum = 0;

    const staffRows = filteredStaff.map((staff) => {
      let presentDays = 0;
      let absentDays = 0;
      let halfDays = 0;
      let totalMinutes = 0;

      Object.entries(staff.attendanceByDate).forEach(([dKey, att]) => {
        // verify date belongs to this month
        const m = moment(dKey);
        if (m.isBetween(start, end, null, '[]')) {
          if (att.status === 'present') {
            presentDays++;
            totalPresentSum++;
          } else if (att.status === 'absent') {
            absentDays++;
            totalAbsentSum++;
          } else if (att.status === 'half-day' || att.status === 'on-leave') {
            halfDays++;
            totalHalfDaySum++;
          }
          totalMinutes += att.durationMinutes || 0;
        }
      });

      const totalMarkedDays = presentDays + absentDays + halfDays;
      const baseDays = totalMarkedDays > 0 ? totalMarkedDays : clinicRecordedDays;
      const effectivePresent = presentDays + halfDays * 0.5;
      const rate =
        baseDays > 0 ? ((effectivePresent / baseDays) * 100).toFixed(1) : '0.0';

      const hoursNum = (totalMinutes / 60).toFixed(1);
      const avgHoursPerDay =
        presentDays > 0 ? (totalMinutes / (presentDays * 60)).toFixed(1) : '0.0';

      return {
        ...staff,
        presentDays,
        absentDays,
        halfDays,
        totalMarkedDays,
        rate,
        totalHours: `${hoursNum} hrs`,
        avgHoursPerDay: `${avgHoursPerDay} hrs`,
      };
    });

    const totalStaff = filteredStaff.length;
    const totalRecordedShifts = totalPresentSum + totalAbsentSum + totalHalfDaySum;
    const overallRate =
      totalRecordedShifts > 0
        ? (
            ((totalPresentSum + totalHalfDaySum * 0.5) / totalRecordedShifts) *
            100
          ).toFixed(1)
        : '0.0';

    return {
      staffRows,
      metrics: {
        totalStaff,
        daysInMonth: totalDays,
        clinicRecordedDays,
        overallRate,
        totalPresentSum,
        totalAbsentSum,
        totalHalfDaySum,
      },
    };
  }, [activeTab, currentDate, dateRange, filteredStaff, submissions]);

  // Date Navigation handlers
  const handlePrev = () => {
    if (activeTab === 'daily') {
      setCurrentDate((prev) => prev.clone().subtract(1, 'day'));
    } else if (activeTab === 'weekly') {
      setCurrentDate((prev) => prev.clone().subtract(1, 'week'));
    } else {
      setCurrentDate((prev) => prev.clone().subtract(1, 'month'));
    }
  };

  const handleNext = () => {
    if (activeTab === 'daily') {
      setCurrentDate((prev) => prev.clone().add(1, 'day'));
    } else if (activeTab === 'weekly') {
      setCurrentDate((prev) => prev.clone().add(1, 'week'));
    } else {
      setCurrentDate((prev) => prev.clone().add(1, 'month'));
    }
  };

  const handleGoToday = () => {
    setCurrentDate(moment());
  };

  // Builds the rows for the current view; shared by the CSV and Excel exports.
  const buildExportRows = () => {
    if (activeTab === 'daily') {
      const rows = [
        ['Staff Name', 'Position', 'Status', 'Check In', 'Check Out', 'Working Duration', 'Notes', 'Date'],
      ];
      filteredStaff.forEach((s) => {
        const att = s.attendanceByDate[dateRange.startDate];
        rows.push([
          s.name,
          s.position,
          att ? att.status.toUpperCase() : 'NOT MARKED',
          att ? att.checkIn : '-',
          att ? att.checkOut : '-',
          att ? att.durationText : '-',
          att ? att.notes : '',
          dateRange.startDate,
        ]);
      });
      return { name: `attendance_daily_${dateRange.startDate}`, rows };
    } else if (activeTab === 'weekly') {
      const dayHeaders = (dateRange.days || []).map(
        (d) => `${d.format('ddd (D MMM)')}`
      );
      const rows = [
        ['Staff Name', 'Position', ...dayHeaders, 'Present Days', 'Absent Days', 'Half-Days', 'Attendance Rate %'],
      ];
      weeklyData.staffRows.forEach((s) => {
        const dayStatuses = s.dailyBreakdown.map((d) =>
          d.record ? `${d.record.status.toUpperCase()} (${d.record.checkIn}-${d.record.checkOut})` : '-'
        );
        rows.push([
          s.name,
          s.position,
          ...dayStatuses,
          s.presentDays,
          s.absentDays,
          s.halfDays,
          `${s.rate}%`,
        ]);
      });
      return { name: `attendance_weekly_${dateRange.startDate}_to_${dateRange.endDate}`, rows };
    } else {
      const rows = [
        ['Staff Name', 'Position', 'Join Date', 'Present Days', 'Absent Days', 'Half Days / Leave', 'Total Logged Days', 'Attendance Rate %', 'Total Working Hours', 'Avg Hours/Day', 'Month'],
      ];
      monthlyData.staffRows.forEach((s) => {
        rows.push([
          s.name,
          s.position,
          s.joinDate,
          s.presentDays,
          s.absentDays,
          s.halfDays,
          s.totalMarkedDays,
          `${s.rate}%`,
          s.totalHours,
          s.avgHoursPerDay,
          dateRange.label,
        ]);
      });
      return { name: `attendance_monthly_${currentDate.format('YYYY_MM')}`, rows };
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const { name, rows } = buildExportRows();
    downloadCSV(`${name}.csv`, rows);
  };

  // Export to Excel (.xlsx) — same rows as the CSV, header row becomes column labels.
  const handleExportExcel = () => {
    const { name, rows } = buildExportRows();
    const [head = [], ...body] = rows;
    const objects = body.map((r) => Object.fromEntries(head.map((label, i) => [label, r[i] ?? ''])));
    exportToExcel(objects, name, head.map((label) => ({ key: label, label })), 'Attendance');
  };

  const downloadCSV = (filename, rows) => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      rows
        .map((e) =>
          e
            .map((val) => {
              const text = (val === null || val === undefined ? '' : String(val)).replace(/"/g, '""');
              return `"${text}"`;
            })
            .join(',')
        )
        .join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper for Status Badge styling
  const renderStatusBadge = (status) => {
    const norm = String(status || '').toLowerCase();
    switch (norm) {
      case 'present':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
            ● Present
          </span>
        );
      case 'absent':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-300">
            ● Absent
          </span>
        );
      case 'half-day':
      case 'halfday':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-300">
            ● Half-Day
          </span>
        );
      case 'on-leave':
      case 'leave':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
            ● On-Leave
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
            Not Marked
          </span>
        );
    }
  };

  return (
    <RightWrapper>
      <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
        {/* ===== TOP HEADER ===== */}
        <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FaUsers className="text-blue-600" />
                Attendance Counts & Reports
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Monitor and analyze staff attendance counts across daily, weekly, and monthly intervals.
              </p>
            </div>

            {/* Interval Tabs */}
            <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 self-start lg:self-auto">
              <button
                type="button"
                onClick={() => setActiveTab('daily')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === 'daily'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FaCalendarDay />
                Daily Count
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('weekly')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === 'weekly'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FaCalendarWeek />
                Weekly Count
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('monthly')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === 'monthly'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FaCalendarAlt />
                Monthly Count
              </button>
            </div>
          </div>

          {/* ===== DATE NAVIGATION & ACTIONS BAR ===== */}
          <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
            {/* Date Navigator */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrev}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition"
                title="Previous"
              >
                <FaChevronLeft className="w-3.5 h-3.5" />
              </button>

              {activeTab === 'daily' && (
                <input
                  type="date"
                  value={currentDate.format('YYYY-MM-DD')}
                  onChange={(e) => {
                    if (e.target.value) setCurrentDate(moment(e.target.value));
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 bg-white"
                />
              )}

              <span className="text-sm font-bold text-gray-800 px-2">
                {dateRange.label}
              </span>

              <button
                type="button"
                onClick={handleNext}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition"
                title="Next"
              >
                <FaChevronRight className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={handleGoToday}
                className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 transition"
              >
                Current {activeTab === 'daily' ? 'Day' : activeTab === 'weekly' ? 'Week' : 'Month'}
              </button>
            </div>

            {/* Right Tools: Refresh & Export */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition shadow-xs"
              >
                <FaSync className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>

              <button
                type="button"
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition shadow-xs"
              >
                <FaFileDownload />
                Export CSV
              </button>

              <button
                type="button"
                onClick={handleExportExcel}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition shadow-xs"
              >
                <FaFileDownload />
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* ===== KPI SUMMARY CARDS ===== */}
        {activeTab === 'daily' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-lg">
                <FaUsers />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Staff</p>
                <p className="text-xl font-bold text-gray-800">{dailyMetrics.totalStaff}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-lg">
                <FaUserCheck />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Present Today</p>
                <p className="text-xl font-bold text-emerald-600">
                  {dailyMetrics.present}
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    ({dailyMetrics.presentRate}%)
                  </span>
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center text-lg">
                <FaUserTimes />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Absent Today</p>
                <p className="text-xl font-bold text-rose-600">{dailyMetrics.absent}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center text-lg">
                <FaClock />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Half-Day / Leave</p>
                <p className="text-xl font-bold text-amber-600">{dailyMetrics.halfDay}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'weekly' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-lg">
                <FaUsers />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Active Staff</p>
                <p className="text-xl font-bold text-gray-800">{weeklyData.metrics.totalStaff}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-lg">
                <FaCalendarWeek />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Weekly Attendance Rate</p>
                <p className="text-xl font-bold text-purple-700">{weeklyData.metrics.avgAttendanceRate}%</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-lg">
                <FaUserCheck />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Present Days</p>
                <p className="text-xl font-bold text-emerald-600">{weeklyData.metrics.totalPresentShifts}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center text-lg">
                <FaUserTimes />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Absent Days</p>
                <p className="text-xl font-bold text-rose-600">{weeklyData.metrics.totalAbsentShifts}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'monthly' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-lg">
                <FaCalendarAlt />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Days in Month</p>
                <p className="text-xl font-bold text-gray-800">{monthlyData.metrics.daysInMonth} Days</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-lg">
                <FaUserCheck />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Monthly Attendance Rate</p>
                <p className="text-xl font-bold text-emerald-600">{monthlyData.metrics.overallRate}%</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center text-lg">
                <FaClock />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Present Days</p>
                <p className="text-xl font-bold text-teal-600">{monthlyData.metrics.totalPresentSum}</p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center text-lg">
                <FaUserTimes />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Absent Days</p>
                <p className="text-xl font-bold text-rose-600">{monthlyData.metrics.totalAbsentSum}</p>
              </div>
            </div>
          </div>
        )}

        {/* ===== SEARCH & FILTER CONTROLS ===== */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-xs flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <FaSearch className="absolute left-3 top-3 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search staff by name or position..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600">Role:</label>
            <select
              value={selectedPosition}
              onChange={(e) => setSelectedPosition(e.target.value)}
              className="border border-gray-300 rounded-lg text-xs py-1.5 px-2 bg-white"
            >
              <option value="ALL">All Roles</option>
              {positionsList.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
          </div>

          {activeTab === 'daily' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600">Status:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="border border-gray-300 rounded-lg text-xs py-1.5 px-2 bg-white"
              >
                <option value="ALL">All Statuses</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="half-day">Half-Day / Leave</option>
                <option value="not-marked">Not Marked</option>
              </select>
            </div>
          )}

          <div className="ml-auto text-xs text-gray-500 font-medium">
            Showing <span className="font-bold text-gray-800">{filteredStaff.length}</span> staff members
          </div>
        </div>

        {/* ===== TABLES BY ACTIVE TAB ===== */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 shadow-xs">
            <FaSync className="animate-spin text-3xl mx-auto mb-3 text-blue-500" />
            <p className="text-sm font-semibold">Loading attendance records...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
            {/* DAILY TABLE */}
            {activeTab === 'daily' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                      <th className="py-3 px-4">Staff Member</th>
                      <th className="py-3 px-4">Role / Position</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-center">Punch In</th>
                      <th className="py-3 px-4 text-center">Punch Out</th>
                      <th className="py-3 px-4 text-center">Duration</th>
                      <th className="py-3 px-4">Remarks / Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {filteredStaff.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-gray-400">
                          No staff attendance records found for this day.
                        </td>
                      </tr>
                    ) : (
                      filteredStaff.map((staff) => {
                        const att = staff.attendanceByDate[dateRange.startDate];
                        return (
                          <tr key={staff.id} className="hover:bg-slate-50/80 transition">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">
                                  {staff.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-semibold text-gray-800">{staff.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-medium">
                                {staff.position}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {renderStatusBadge(att?.status)}
                            </td>
                            <td className="py-3 px-4 text-center font-mono text-gray-700">
                              {att?.checkIn || '-'}
                            </td>
                            <td className="py-3 px-4 text-center font-mono text-gray-700">
                              {att?.checkOut || '-'}
                            </td>
                            <td className="py-3 px-4 text-center font-semibold text-gray-700">
                              {att?.durationText || '-'}
                            </td>
                            <td className="py-3 px-4 text-gray-500 italic">
                              {att?.notes || '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* WEEKLY TABLE */}
            {activeTab === 'weekly' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                      <th className="py-3 px-4 sticky left-0 bg-gray-50 z-10 w-48">Staff Member</th>
                      {(dateRange.days || []).map((day) => {
                        const isToday = day.isSame(moment(), 'day');
                        return (
                          <th
                            key={day.format('YYYY-MM-DD')}
                            className={`py-3 px-3 text-center min-w-[95px] ${
                              isToday ? 'bg-blue-50 text-blue-700 font-extrabold' : ''
                            }`}
                          >
                            <div>{day.format('ddd')}</div>
                            <div className="text-xs">{day.format('D MMM')}</div>
                          </th>
                        );
                      })}
                      <th className="py-3 px-3 text-center bg-gray-100 text-emerald-800">Present</th>
                      <th className="py-3 px-3 text-center bg-gray-100 text-rose-800">Absent</th>
                      <th className="py-3 px-3 text-center bg-gray-100 text-blue-800">Rate %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {weeklyData.staffRows.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="py-8 text-center text-gray-400">
                          No staff attendance records found for this week.
                        </td>
                      </tr>
                    ) : (
                      weeklyData.staffRows.map((staff) => (
                        <tr key={staff.id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3 px-4 sticky left-0 bg-white z-10 font-semibold text-gray-800 border-r border-gray-100">
                            <div>{staff.name}</div>
                            <div className="text-[10px] text-gray-500 font-normal">{staff.position}</div>
                          </td>

                          {staff.dailyBreakdown.map((d) => (
                            <td
                              key={d.dateStr}
                              className={`py-2 px-2 text-center border-r border-gray-100 ${
                                d.isToday ? 'bg-blue-50/40' : ''
                              }`}
                            >
                              {d.record ? (
                                <div className="flex flex-col items-center gap-1">
                                  {renderStatusBadge(d.record.status)}
                                  {d.record.status === 'present' && (
                                    <span className="text-[10px] text-gray-500 font-mono">
                                      {d.record.checkIn}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          ))}

                          <td className="py-3 px-3 text-center font-bold text-emerald-600 bg-emerald-50/30">
                            {staff.presentDays} d
                          </td>
                          <td className="py-3 px-3 text-center font-bold text-rose-600 bg-rose-50/30">
                            {staff.absentDays} d
                          </td>
                          <td className="py-3 px-3 text-center font-bold text-blue-700 bg-blue-50/30">
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] ${
                                Number(staff.rate) >= 80
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : Number(staff.rate) >= 60
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {staff.rate}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* MONTHLY TABLE */}
            {activeTab === 'monthly' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                      <th className="py-3 px-4">Staff Member</th>
                      <th className="py-3 px-4">Position</th>
                      <th className="py-3 px-4 text-center">Present Days</th>
                      <th className="py-3 px-4 text-center">Absent Days</th>
                      <th className="py-3 px-4 text-center">Half-Days / Leave</th>
                      <th className="py-3 px-4 text-center">Logged Days</th>
                      <th className="py-3 px-4 text-center">Total Hours</th>
                      <th className="py-3 px-4 text-center">Avg Hours/Day</th>
                      <th className="py-3 px-4 text-center w-40">Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {monthlyData.staffRows.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="py-8 text-center text-gray-400">
                          No staff attendance records found for this month.
                        </td>
                      </tr>
                    ) : (
                      monthlyData.staffRows.map((staff) => {
                        const rateNum = Number(staff.rate) || 0;
                        const progressColor =
                          rateNum >= 85
                            ? 'bg-emerald-500'
                            : rateNum >= 70
                            ? 'bg-amber-500'
                            : 'bg-rose-500';

                        return (
                          <tr key={staff.id} className="hover:bg-slate-50/80 transition">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">
                                  {staff.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-semibold text-gray-800">{staff.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-medium">
                                {staff.position}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center font-bold text-emerald-600">
                              {staff.presentDays}
                            </td>
                            <td className="py-3 px-4 text-center font-bold text-rose-600">
                              {staff.absentDays}
                            </td>
                            <td className="py-3 px-4 text-center font-semibold text-amber-600">
                              {staff.halfDays}
                            </td>
                            <td className="py-3 px-4 text-center text-gray-700">
                              {staff.totalMarkedDays}
                            </td>
                            <td className="py-3 px-4 text-center font-mono font-medium text-gray-700">
                              {staff.totalHours}
                            </td>
                            <td className="py-3 px-4 text-center font-mono text-gray-600">
                              {staff.avgHoursPerDay}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className={`h-2 rounded-full ${progressColor}`}
                                    style={{ width: `${Math.min(rateNum, 100)}%` }}
                                  ></div>
                                </div>
                                <span className="font-bold text-[11px] text-gray-700 w-10 text-right">
                                  {staff.rate}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </RightWrapper>
  );
};

export default AttendanceCalendar;